import React, { ComponentProps, Dispatch, SetStateAction, useContext, useState } from 'react';
import { Supplier, SupplierPriceList } from '../../graphql/entities';
import { default as Form } from '../../components/connectedForm';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { SuccessButton } from '../../components/connectedForm/Button';
import SupplierPriceListModal from '../../components/modals/SupplierPriceListModal';
import PriceListWorkspace from '../PriceListWorkspace';
import WorkspaceContext from '../../components/Context/WorkspaceContext';
import { ISupplier, Maybe } from '../../graphql/schema/p4types';
import HeaderBlock from '../../components/connectedForm/HeaderBlock';
import FluentModal from '../../components/modals/FluentModal';

const VIEW_SCREEN_NAME = '/suppliers/SupplierPriceList/Workspace';

const SUPPLIER_PRICE_LIST_FIELDNAMES = [
  'product.reference',
  'product.description',
  'product.latestCostPrice',
  'product.costUnitDescription',
  'supplierPricePotential.nettPrice',
  'product.sellingUnitDescription',
  'product.vatProductCategory.description',
  'pricePotentialList',
];

const CustomWorkspaceHeader = ({
  supplier,
  setSupplier,
}: {
  supplier: Maybe<ISupplier>;
  setSupplier: Dispatch<SetStateAction<Maybe<ISupplier>>>;
}) => {
  const { selectionState, onRowSelect } = useContext(WorkspaceContext);
  const [printIsOpen, setPrintIsOpen] = React.useState(false);

  const onSupplierChange = (value: Maybe<ISupplier>) => {
    setSupplier(value);
    onRowSelect?.([], [], false);
  };

  const printButton = (
    <SuccessButton
      disabled={!supplier}
      onClick={(e) => {
        setPrintIsOpen(true);
      }}
    >
      Print
    </SuccessButton>
  );

  return (
    <>
      <FluentModal isOpen={printIsOpen}>
        <div style={{ width: '500px', padding: '1em' }}>
          <SupplierPriceListModal
            multiValue={true}
            allowEmailTo={true}
            supplier={normalizeVariables(supplier)}
            selectedAll={selectionState.selectedAll}
            workspaceUrl={VIEW_SCREEN_NAME}
            filters={selectionState.filters}
            onClose={() => setPrintIsOpen(false)}
          />
        </div>
      </FluentModal>
      <HeaderBlock name={SupplierPriceList.title} extraBtns={printButton} entity={SupplierPriceList} />
      <Form.Form>
        <Form.Row>
          <Form.Fieldset cols={1 / 2}>
            <Form.Legend label="General" />
            <QueryDropdown
              data-testid={'price-list-supplier'}
              {...Supplier.dropdown}
              includeLabel={false}
              value={supplier}
              onChange={onSupplierChange}
              readOnly={false}
              clearable={false}
            />
          </Form.Fieldset>
        </Form.Row>
      </Form.Form>
    </>
  );
};

const PriceListWorkspaceWrapper = (props: Omit<ComponentProps<typeof PriceListWorkspace>, 'fieldNames' | 'entity'>) => {
  return <PriceListWorkspace fieldNames={SUPPLIER_PRICE_LIST_FIELDNAMES} entity={SupplierPriceList} {...props} />;
};

export default (props: ComponentProps<typeof PriceListWorkspaceWrapper>) => {
  const [supplier, setSupplier] = useState<Maybe<ISupplier>>(null);

  if (props.embeddedView) {
    // If we have an embedded prop, return the Workspace directly.
    return <PriceListWorkspaceWrapper {...props} />;
  }

  // Otherwise, we're displaying as a main screen, so add the custom header for configuring the Customer.
  // (We still pass embedded to the actual Workspace because we want some common side effects of using that prop).
  return (
    <PriceListWorkspaceWrapper
      embeddedView={true}
      viewScreenName={VIEW_SCREEN_NAME}
      additionalVariables={{
        supplier: normalizeVariables(supplier),
      }}
      enableRowSelect={true}
      customHeader={<CustomWorkspaceHeader supplier={supplier} setSupplier={setSupplier} />}
      ignoreContainerWidth={true}
    />
  );
};
